import {
  PARTNER_CATEGORIES,
  PARTNER_COUNTIES,
  countyForZip,
  countyLabel,
  findPartnerDuplicates,
  formatE164,
  normalizeCardExtraction,
  normalizeLineOfWork,
  partnerCategoryLabel,
  partnerDisplayName,
  todayCentral,
  toE164,
  type CardField,
  type NormalizedCard,
} from "@roof-hawk/core";
import { useEffect, useRef, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { prepareCardImage, type PreparedCard } from "../../lib/cardImage";
import { LineOfWorkField } from "./LineOfWorkField";
import { navigate, useAsync } from "../../lib/hooks";
import {
  fetchPartners,
  insertPartner,
  readBusinessCard,
  setConsent,
  updatePartner,
  uploadCardImage,
} from "../../lib/partnerQueries";
import { useSession } from "../../session";

/**
 * Scan a card, check what came out of it, save.
 *
 * The screen is built around one fact: **the extraction is a guess and the rep
 * is standing in front of the person it is about.** Everything follows from
 * that — the photographs stay on screen beside the form so a doubtful field can
 * be checked by eye, the fields the model was unsure of are highlighted and
 * counted, and the form works perfectly well with no card at all.
 *
 * The consent block is the part that cannot be skipped past. A partner saved
 * without it is saved, and is excluded from every campaign until somebody asks
 * them — which is the correct behaviour and the reason the box is not ticked by
 * default.
 */

type Stage = "capture" | "review";

interface CardSlot {
  file: PreparedCard | null;
  error: string | null;
}

const BLANK: NormalizedCard = normalizeCardExtraction({});

export function NewPartner({
  network,
}: {
  /**
   * `company_settings.partners_enabled`. Off, this is the card file's scan
   * screen: no texting consent and no owner, because nobody is texted
   * (ADR 0262). Whoever scans the card is kept as its owner, so turning the
   * network on later finds every card with somebody's phone behind it.
   */
  network: boolean;
}) {
  const { profileId, people } = useSession();
  const existing = useAsync(fetchPartners, []);

  const [stage, setStage] = useState<Stage>("capture");
  const [front, setFront] = useState<CardSlot>({ file: null, error: null });
  const [back, setBack] = useState<CardSlot>({ file: null, error: null });
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);

  const [card, setCard] = useState<NormalizedCard>(BLANK);
  const [raw, setRaw] = useState<Record<string, unknown> | null>(null);
  const [metAt, setMetAt] = useState("");
  // Today in Birmingham, not today in UTC — after 7pm Central those are
  // different days, and the card was scanned on this one.
  const [metOn, setMetOn] = useState(todayCentral);
  const [notes, setNotes] = useState("");
  const [ownerId, setOwnerId] = useState(profileId ?? "");
  const [consented, setConsented] = useState(false);
  const [consentNote, setConsentNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [overrideDuplicate, setOverrideDuplicate] = useState(false);

  // Object URLs are a leak if nobody revokes them, and this screen makes two
  // per card retaken.
  const urls = useRef<string[]>([]);
  useEffect(() => {
    const held = urls.current;
    return () => held.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  async function takePhoto(
    side: "front" | "back",
    file: File | undefined,
  ): Promise<void> {
    if (!file) return;
    const set = side === "front" ? setFront : setBack;
    try {
      const prepared = await prepareCardImage(file);
      urls.current.push(prepared.previewUrl);
      set({ file: prepared, error: null });
    } catch (error) {
      set({
        file: null,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  async function readTheCard(): Promise<void> {
    if (!front.file) return;
    setReading(true);
    setReadError(null);
    try {
      const extraction = await readBusinessCard({
        front: { media_type: front.file.media_type, data: front.file.base64 },
        back: back.file
          ? { media_type: back.file.media_type, data: back.file.base64 }
          : null,
      });
      setRaw(extraction);
      setCard(normalizeCardExtraction(extraction));
      setStage("review");
    } catch (error) {
      // The card reader being down must not cost the rep the photographs or
      // the trip. Straight to the form, empty, with the pictures beside it.
      setReadError(error instanceof Error ? error.message : String(error));
      setStage("review");
    } finally {
      setReading(false);
    }
  }

  const duplicates = findPartnerDuplicates(
    { id: "new", ...card },
    existing.data ?? [],
  );

  const canSave = Boolean(card.first_name || card.last_name || card.company);

  async function save(): Promise<void> {
    setSaving(true);
    setSaveError(null);
    try {
      const partner = await insertPartner({
        owner_profile_id: ownerId || null,
        first_name: card.first_name,
        last_name: card.last_name,
        company: card.company,
        title: card.title,
        line_of_work: normalizeLineOfWork(card.line_of_work),
        category: card.category,
        phone_mobile: card.phone_mobile,
        phone_office: card.phone_office,
        email: card.email,
        website: card.website,
        address_line1: card.address_line1,
        city: card.city,
        state: card.state,
        zip: card.zip,
        county: card.county,
        linkedin: card.linkedin,
        instagram: card.instagram,
        facebook: card.facebook,
        met_at: metAt.trim() || null,
        met_on: metOn || null,
        notes: notes.trim() || null,
        extraction_raw: raw,
        extraction_confidence: card.confidence,
      });

      // The photographs go up after the row exists, because they are keyed by
      // its id. A failure here loses the pictures and keeps the partner, which
      // is the right way round — the details are what the campaign needs.
      const paths: Record<string, string> = {};
      if (front.file) {
        paths.card_front_path = await uploadCardImage(
          partner.id,
          "front",
          front.file.blob,
        );
      }
      if (back.file) {
        paths.card_back_path = await uploadCardImage(
          partner.id,
          "back",
          back.file.blob,
        );
      }
      if (Object.keys(paths).length > 0) {
        await updatePartner(partner.id, paths);
      }

      // Consent last, and through the database function, so the status and the
      // log entry cannot come apart.
      if (network && consented) {
        await setConsent({
          partnerId: partner.id,
          event: "verbal_opt_in",
          actorProfileId: profileId,
          note: consentNote.trim(),
        });
      }

      navigate(`partner=${partner.id}`);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error));
      setSaving(false);
    }
  }

  if (stage === "capture") {
    return (
      <div className="card-capture">
        <div className="listhead">
          <h2>Scan a card</h2>
          <button className="btn-ghost" onClick={() => navigate("partners")}>
            {network ? "Back to partners" : "Back to the cards"}
          </button>
        </div>

        <div className="capture-slots">
          <CaptureSlot
            label="Front"
            hint="Required"
            slot={front}
            onPick={(file) => void takePhoto("front", file)}
          />
          <CaptureSlot
            label="Back"
            hint="Optional — often the mobile number"
            slot={back}
            onPick={(file) => void takePhoto("back", file)}
          />
        </div>

        <ErrorNote error={readError} />

        <div className="capture-actions">
          <button
            className="btn-primary"
            disabled={!front.file || reading}
            onClick={() => void readTheCard()}
          >
            {reading ? "Reading the card…" : "Read the card"}
          </button>
          <button className="btn-ghost" onClick={() => setStage("review")}>
            Type it in instead
          </button>
        </div>

        <p className="capture-note">
          The photographs are kept, so a doubtful field can be checked against
          the card months later.
        </p>
      </div>
    );
  }

  return (
    <div className="card-review">
      <div className="listhead">
        <h2>
          {partnerDisplayName(card) === "—"
            ? network
              ? "New partner"
              : "New card"
            : partnerDisplayName(card)}
        </h2>
        <button className="btn-ghost" onClick={() => setStage("capture")}>
          Back to the photographs
        </button>
      </div>

      <ErrorNote error={readError} />

      <div className="review-split">
        <div className="review-form">
          <ToCheck card={card} />

          <div className="fieldgrid">
            <Field
              label="First name"
              field="first_name"
              card={card}
              setCard={setCard}
            />
            <Field
              label="Last name"
              field="last_name"
              card={card}
              setCard={setCard}
            />
            <Field
              label="Company"
              field="company"
              card={card}
              setCard={setCard}
              wide
            />
            <Field
              label="Title"
              field="title"
              card={card}
              setCard={setCard}
              wide
            />

            <LineOfWorkField
              id="pline"
              value={card.line_of_work ?? ""}
              onChange={(value) =>
                setCard((current) => ({ ...current, line_of_work: value }))
              }
              onCategory={(category) =>
                setCard((current) => ({ ...current, category }))
              }
              lowConfidence={card.confidence.line_of_work === "low"}
            />

            {/* The category decides which monthly texts a partner gets, so it
                only matters while the partner network is on (ADR 0262). */}
            {network && (
              <div
                className={`field${card.confidence.category === "low" ? " tocheck" : ""}`}
              >
                <label htmlFor="pcat">Category, for texts</label>
                <select
                  id="pcat"
                  value={card.category}
                  onChange={(e) =>
                    setCard({
                      ...card,
                      category: e.target.value as NormalizedCard["category"],
                    })
                  }
                >
                  {PARTNER_CATEGORIES.map((value) => (
                    <option key={value} value={value}>
                      {partnerCategoryLabel(value)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {network && (
              <div className="field">
                <label htmlFor="powner">Owner</label>
                {/* Whose phone the texts will leave from. Not an assignment
                  rule — the partner has this person's number saved. */}
                <select
                  id="powner"
                  value={ownerId}
                  onChange={(e) => setOwnerId(e.target.value)}
                >
                  <option value="">Nobody yet</option>
                  {people
                    .filter((person) => person.active !== false)
                    .map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.full_name}
                      </option>
                    ))}
                </select>
              </div>
            )}

            <PhoneField
              label={network ? "Mobile — the number texts go to" : "Mobile"}
              field="phone_mobile"
              card={card}
              setCard={setCard}
            />
            <PhoneField
              label="Office"
              field="phone_office"
              card={card}
              setCard={setCard}
            />

            <Field
              label="Email"
              field="email"
              card={card}
              setCard={setCard}
              wide
            />
            <Field
              label="Website"
              field="website"
              card={card}
              setCard={setCard}
              wide
            />

            <Field
              label="Address"
              field="address_line1"
              card={card}
              setCard={setCard}
              wide
            />
            <Field label="City" field="city" card={card} setCard={setCard} />
            <Field label="State" field="state" card={card} setCard={setCard} />

            <div
              className={`field${card.confidence.zip === "low" ? " tocheck" : ""}`}
            >
              <label htmlFor="pzip">ZIP</label>
              <input
                id="pzip"
                value={card.zip ?? ""}
                onChange={(e) => {
                  const zip = e.target.value || null;
                  // The county follows the ZIP until somebody overrides it.
                  setCard({ ...card, zip, county: countyForZip(zip) });
                }}
              />
            </div>

            <div className="field">
              <label htmlFor="pcounty">County</label>
              <select
                id="pcounty"
                value={card.county}
                onChange={(e) =>
                  setCard({
                    ...card,
                    county: e.target.value as NormalizedCard["county"],
                  })
                }
              >
                {PARTNER_COUNTIES.map((value) => (
                  <option key={value} value={value}>
                    {countyLabel(value)}
                  </option>
                ))}
              </select>
            </div>

            <div className="field full">
              <label htmlFor="pmet">Where you met</label>
              <input
                id="pmet"
                value={metAt}
                placeholder="EPIC Mountainbrook, Chamber lunch…"
                onChange={(e) => setMetAt(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="pmeton">When</label>
              <input
                id="pmeton"
                type="date"
                value={metOn}
                onChange={(e) => setMetOn(e.target.value)}
              />
            </div>

            <Field
              label="LinkedIn"
              field="linkedin"
              card={card}
              setCard={setCard}
            />
            <Field
              label="Instagram"
              field="instagram"
              card={card}
              setCard={setCard}
            />
            <Field
              label="Facebook"
              field="facebook"
              card={card}
              setCard={setCard}
            />

            <div className="field full">
              <label htmlFor="pnotes">Notes</label>
              <textarea
                id="pnotes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {card.unrecognized_text && (
            <div className="unplaced">
              <b>Also on the card</b>
              <p>{card.unrecognized_text}</p>
            </div>
          )}

          {network && (
            <ConsentBlock
              firstName={card.first_name}
              consented={consented}
              setConsented={setConsented}
              note={consentNote}
              setNote={setConsentNote}
            />
          )}

          {duplicates.length > 0 && !overrideDuplicate && (
            <div className="dupe">
              <b>You may already have this person</b>
              <ul>
                {duplicates.map((row) => (
                  <li key={row.id}>
                    <button
                      className="linky"
                      onClick={() => navigate(`partner=${row.id}`)}
                    >
                      {partnerDisplayName(row)}
                      {row.company ? ` at ${row.company}` : ""}
                    </button>
                  </li>
                ))}
              </ul>
              <button
                className="btn-ghost"
                onClick={() => setOverrideDuplicate(true)}
              >
                Save anyway
              </button>
            </div>
          )}

          <ErrorNote error={saveError} />

          <div className="capture-actions">
            <button
              className="btn-primary"
              disabled={
                !canSave ||
                saving ||
                (network && consented && consentNote.trim() === "") ||
                (duplicates.length > 0 && !overrideDuplicate)
              }
              onClick={() => void save()}
            >
              {saving ? "Saving…" : network ? "Save partner" : "Save card"}
            </button>
            {!canSave && (
              <span className="sub">A name or a company, at least.</span>
            )}
            {network && consented && consentNote.trim() === "" && (
              <span className="sub">Say where they said yes.</span>
            )}
          </div>
        </div>

        <div className="review-cards">
          {front.file && (
            <img src={front.file.previewUrl} alt="The front of the card" />
          )}
          {back.file && (
            <img src={back.file.previewUrl} alt="The back of the card" />
          )}
          {!front.file && (
            <div className="placeholder">
              <b>No photograph</b>
              Typed in by hand.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- the pieces -- */

function CaptureSlot({
  label,
  hint,
  slot,
  onPick,
}: {
  label: string;
  hint: string;
  slot: CardSlot;
  onPick: (file: File | undefined) => void;
}) {
  return (
    <label className={`capture-slot${slot.file ? " filled" : ""}`}>
      {slot.file ? (
        <img
          src={slot.file.previewUrl}
          alt={`The ${label.toLowerCase()} of the card`}
        />
      ) : (
        <span className="capture-empty">
          <b>{label}</b>
          {hint}
        </span>
      )}
      {/* `capture="environment"` opens the back camera straight into the
          viewfinder on a phone, and is ignored on a desktop where it falls
          back to a file picker — which is what somebody importing an old
          photograph wants anyway. */}
      <input
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => onPick(e.target.files?.[0])}
      />
      {slot.error && <span className="err">{slot.error}</span>}
      {slot.file && <span className="capture-retake">Tap to retake</span>}
    </label>
  );
}

function ToCheck({ card }: { card: NormalizedCard }) {
  const fields = (Object.keys(card.confidence) as CardField[]).filter(
    (field) => card.confidence[field] === "low",
  );
  if (fields.length === 0) return null;
  return (
    <div className="tocheck-note">
      <b>
        {fields.length} {fields.length === 1 ? "field" : "fields"} worth
        checking
      </b>
      They are highlighted below. The card is on the right.
    </div>
  );
}

function Field({
  label,
  field,
  card,
  setCard,
  wide,
}: {
  label: string;
  field: CardField;
  card: NormalizedCard;
  setCard: (card: NormalizedCard) => void;
  wide?: boolean;
}) {
  const id = `p-${field}`;
  const value = card[field];
  return (
    <div
      className={`field${wide ? " full" : ""}${
        card.confidence[field] === "low" ? " tocheck" : ""
      }`}
    >
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => setCard({ ...card, [field]: e.target.value || null })}
      />
    </div>
  );
}

/**
 * A phone box that stores E.164 and shows something readable.
 *
 * Not `PhoneInput` from components/ui: that one punctuates as you type and
 * hands back the punctuation, which is right for a customer's number stored as
 * typed and wrong here, where the column takes E.164 or nothing.
 *
 * **The normalised value is committed on every keystroke, not on blur.** An
 * earlier version normalised on blur and lost the number of anybody who typed
 * it and pressed Save with the caret still in the box — silently, because the
 * field went on showing what they had typed. Blur now only drops the display
 * override; the value behind it is already right.
 */
function PhoneField({
  label,
  field,
  card,
  setCard,
}: {
  label: string;
  field: "phone_mobile" | "phone_office";
  card: NormalizedCard;
  setCard: (card: NormalizedCard) => void;
}) {
  const [typing, setTyping] = useState<string | null>(null);
  const id = `p-${field}`;
  const stored = card[field];
  const shown = typing ?? formatE164(stored) ?? "";
  const bad =
    typing !== null && typing.trim() !== "" && toE164(typing) === null;

  return (
    <div
      className={`field${card.confidence[field] === "low" || bad ? " tocheck" : ""}`}
    >
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="tel"
        inputMode="tel"
        value={shown}
        onChange={(e) => {
          setTyping(e.target.value);
          setCard({ ...card, [field]: toE164(e.target.value) });
        }}
        onBlur={() => setTyping(null)}
      />
      {bad && <span className="sub">That is not a whole US number yet.</span>}
    </div>
  );
}

/**
 * The consent block.
 *
 * Unticked by default and it stays that way. There is no webhook on a personal
 * phone to catch a STOP, so a spoken yes recorded here is the only evidence
 * this feature will ever have — and a checkbox that defaults to ticked records
 * nothing except that somebody clicked past a form.
 */
export function ConsentBlock({
  firstName,
  consented,
  setConsented,
  note,
  setNote,
}: {
  firstName: string | null;
  consented: boolean;
  setConsented: (value: boolean) => void;
  note: string;
  setNote: (value: string) => void;
}) {
  const who = firstName?.trim() || "They";
  return (
    <div className="consent">
      <label className="consent-check">
        <input
          type="checkbox"
          checked={consented}
          onChange={(e) => setConsented(e.target.checked)}
        />
        <span>
          {who} said it&apos;s OK to text them roofing and storm updates, and
          referrals.
        </span>
      </label>

      {consented ? (
        <div className="field full">
          <label htmlFor="consent-note">
            Where and when they said it <span className="req">*</span>
          </label>
          <input
            id="consent-note"
            value={note}
            placeholder="EPIC 8/26, in person"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      ) : (
        <p className="sub">
          Saved either way. Until somebody asks them, they are left out of every
          batch and Text now is disabled — which is the point.
        </p>
      )}
    </div>
  );
}
